import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ArrowUpRight } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/components/Logo";

const Header = ({ onOpenAuth }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { label: t("nav.courses"), to: "/#cursos", key: "courses" },
    { label: t("nav.why"), to: "/#porque", key: "why" },
    { label: "Blog", to: "/blog", key: "blog" },
    { label: t("nav.faq"), to: "/#faq", key: "faq" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b border-zinc-200/80"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Logo size="lg" />

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.to}
              className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
              data-testid={`nav-${l.key}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LanguageSwitcher />
          {user ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="header-admin-link"
                  className="text-sm font-medium text-zinc-700 hover:text-[#4F46E5] transition"
                >
                  {t("nav.admin")}
                </Link>
              )}
              <Link
                to="/dashboard"
                data-testid="header-dashboard-link"
                className="text-sm font-medium text-zinc-700 hover:text-[#4F46E5] transition"
              >
                {t("nav.dashboard")}
              </Link>
              <button
                data-testid="header-logout-btn"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="text-sm font-medium text-zinc-500 hover:text-zinc-900"
              >
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="header-login-btn"
                onClick={() => onOpenAuth("login")}
                className="text-sm font-medium text-zinc-700 hover:text-zinc-900"
              >
                {t("nav.login")}
              </button>
              <button
                data-testid="header-cta-btn"
                onClick={() => onOpenAuth("register")}
                className="group inline-flex items-center gap-1 text-sm font-semibold bg-[#4F46E5] text-white px-4 py-2 hover:bg-[#4338CA] transition-all"
              >
                {t("nav.register")}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher compact />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="p-2"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-200 bg-white px-6 py-6 space-y-4">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.to}
              onClick={() => setOpen(false)}
              className="block text-base font-medium text-zinc-700"
            >
              {l.label}
            </a>
          ))}
          {user ? (
            <>
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                className="block text-base font-medium text-[#4F46E5]"
              >
                {t("nav.dashboard")}
              </Link>
              <button
                onClick={() => {
                  logout();
                  setOpen(false);
                }}
                className="text-base font-medium text-zinc-500"
              >
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  onOpenAuth("login");
                  setOpen(false);
                }}
                className="block text-base font-medium text-zinc-700"
              >
                {t("nav.login")}
              </button>
              <button
                onClick={() => {
                  onOpenAuth("register");
                  setOpen(false);
                }}
                className="w-full bg-[#4F46E5] text-white py-3 font-semibold"
              >
                {t("nav.register")}
              </button>
            </>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
